import DOMPurify from 'dompurify';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { MapPin, Building2, Calendar } from 'lucide-react';
import {
  ImpactRecord,
  IMPACT_CATEGORY_BADGE,
  getYoutubeId,
} from '@/hooks/useImpactRecords';

interface Props {
  record: ImpactRecord | null;
  onClose: () => void;
}

export function ImpactDetailModal({ record, onClose }: Props) {
  const ytId = record ? getYoutubeId(record.video_url) : null;

  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
        {record && (
          <>
            {ytId ? (
              <div className="relative aspect-video w-full bg-black flex-shrink-0">
                <iframe
                  src={`https://www.youtube.com/embed/${ytId}`}
                  title={record.title}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
                <Badge className="absolute top-4 left-4 bg-primary/90 text-primary-foreground border-0 z-10">
                  {IMPACT_CATEGORY_BADGE[record.category]}
                </Badge>
              </div>
            ) : record.image_url ? (
              <div className="relative aspect-video w-full overflow-hidden bg-muted flex-shrink-0">
                <img src={record.image_url} alt={record.title} className="w-full h-full object-cover" />
                <Badge className="absolute top-4 left-4 bg-primary/90 text-primary-foreground border-0">
                  {IMPACT_CATEGORY_BADGE[record.category]}
                </Badge>
              </div>
            ) : null}
            <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-4 border-t border-border/50">
              <DialogHeader className="space-y-2">
                <DialogTitle className="text-2xl md:text-3xl leading-tight">{record.title}</DialogTitle>
                {record.subtitle && (
                  <p className="text-base text-muted-foreground">{record.subtitle}</p>
                )}
              </DialogHeader>
              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                {record.company && (
                  <span className="inline-flex items-center gap-1"><Building2 className="h-4 w-4" /> {record.company}</span>
                )}
                {record.location && (
                  <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {record.location}</span>
                )}
                {record.date && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {new Date(record.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                )}
              </div>
              {record.description && (
                <div
                  className="text-foreground/90 leading-relaxed [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-4 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-3 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_li]:my-1 [&_a]:text-primary [&_a]:underline [&_strong]:font-semibold [&_em]:italic [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-3 [&_img]:rounded-lg [&_img]:my-3 [&_hr]:my-4"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(record.description, {
                      ADD_TAGS: ['iframe'],
                      ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target'],
                    }),
                  }}
                />
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
